import { EventName } from '@/plugins/cad/module/core/common/event'
import { StartMode } from '@/plugins/cad/module/core/common/interface'
import type { Controller } from '@/plugins/cad/module/core/controller/Controller'

interface IEventHandler {
  injectTool(tool: BaseTool): void
  ejectTool(): void
}

/**
 * CAD 工具抽象基类，统一生命周期、事件分发与处理器注入。
 */
export abstract class BaseTool {
  /** 当前宿主控制器，由 start 阶段注入 */
  controller!: Controller
  protected startMode!: StartMode

  private eventTypeList: string[] = [
    EventName.onMouseDown,
    EventName.onMouseMove,
    EventName.onDoubleClick,
    EventName.onKeyDown,
    EventName.onKeyUp,
    EventName.onControlsChange,
    EventName.onViewerUpdate,
  ]

  /** 按事件类型聚合的注入处理器映射，Controller 分发事件时遍历调用 */
  readonly injectedMap: { [key: string]: Map<IEventHandler, Function> } = this.eventTypeList.reduce(
    (map, eventType) => {
      map[eventType] = new Map<IEventHandler, Function>()
      return map
    },
    {}
  )

  /**
   * 启动工具并根据启动模式决定是否进入工具栈。
   *
   * @param controller - 宿主控制器
   * @param startMode - 启动模式，默认为独占模式
   * @param args - 透传给子类 initialize 的参数
   */
  start(controller: Controller, startMode = StartMode.alone, ...args: any[]) {
    this.controller = controller
    this.startMode = startMode

    // 根据startMode使用不同的进栈方式，目前都是独享栈
    if (startMode === StartMode.alone) {
      controller.toolStack.alone(this)
      this.initialize(...args)
    } else {
      this.initialize(...args)
    }
  }

  /** 退出工具，独占模式下将自身从工具栈弹出 */
  exit() {
    if (this.startMode === StartMode.none) {
      this.onPause?.()
      this.terminate()
      return
    }
    if (this.controller.toolStack.top === this) {
      this.controller.toolStack.pop()
    }
  }

  onPause?(): void

  onPlay?(): void

  // 初始化-进入服务后需要做的都放在这处理
  protected abstract initialize(...args: any[]): void

  // 终止-退出服务后需要做的都放在这处理
  protected abstract terminate(): void

  /**
   * 注入事件处理器，将其公开的事件方法加入分发表。
   *
   * @param handler - 需要接入当前工具事件流的处理器
   */
  inject(handler: IEventHandler) {
    handler.injectTool(this)
    return this.eventTypeList.forEach((eventType) => {
      handler[eventType] &&
        this.injectedMap[eventType].set(handler, handler[eventType].bind(handler))
    })
  }

  /**
   * 移除已注入的事件处理器。
   *
   * @param handler - 需要从当前工具事件流中解绑的处理器
   */
  eject(handler: IEventHandler) {
    handler.ejectTool()
    return this.eventTypeList.forEach((eventType) => {
      handler[eventType] && this.injectedMap[eventType].delete(handler)
    })
  }
}
